import { fetchUserEvents, parse } from "@/common/helpers/apiClient"
import { browserTimeZone } from "@/common/helpers/browserTimeZone"
import { ExtractedDate } from "@/types/extractedDate"
import { NitteCalendarEvent, PeriodWithId } from "@WombatTechnology/nitte-time-lib"
import { format, startOfDay, endOfDay, min, max } from "date-fns"
import { v4 as uuidv4 } from 'uuid'
import { defineStore } from "pinia"

export type State = {
  inputText: string
  extractedDates: ExtractedDate[]
  isExtracting: boolean
  extractionError: string | null
  cachedEvents: NitteCalendarEvent[]
  isLoadingEvents: boolean
  skipNextExtraction: boolean
}

const fmtLocal = (date: Date) => format(date, "yyyyMMdd'T'HHmmss")
const fmtUtc = (date: Date) => format(date, "yyyyMMdd'T'HHmmss'Z'")

const toGoogleCalendarUrl = (d: ExtractedDate): string | null => {
  if (!d.start || !d.end) return null
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    dates: `${fmtLocal(d.start)}/${fmtLocal(d.end)}`,
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

const toOutlookCalendarUrl = (d: ExtractedDate): string | null => {
  if (!d.start || !d.end) return null
  const params = new URLSearchParams({
    startdt: d.start.toISOString(),
    enddt: d.end.toISOString(),
    path: '/calendar/action/compose',
    rru: 'addevent',
  })
  return `https://outlook.office.com/calendar/action/compose?${params.toString()}`
}

const toIcsBlob = (d: ExtractedDate): Blob | null => {
  if (!d.start || !d.end) return null
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Nitte//text-receive//JA',
    'BEGIN:VEVENT',
    `DTSTART;TZID=${browserTimeZone()}:${fmtLocal(d.start)}`,
    `DTEND;TZID=${browserTimeZone()}:${fmtLocal(d.end)}`,
    'SUMMARY:',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')
  return new Blob([ics], { type: 'text/calendar;charset=utf-8' })
}

export const useExtractedDatesStore = defineStore("extractedDates", {
  state: (): State => {
    return {
      inputText: "",
      extractedDates: [],
      isExtracting: false,
      extractionError: null,
      cachedEvents: [],
      isLoadingEvents: false,
      skipNextExtraction: false,
    }
  },
  actions: {
    async extractDates() {
      if (!this.inputText.trim()) {
        this.extractedDates = []
        this.extractionError = null
        return
      }

      this.isExtracting = true
      this.extractionError = null
      try {
        const results = await parse(
          this.inputText,
          browserTimeZone()
        )
        this.extractedDates = results.map((r) => ({
          id: uuidv4(),
          start: r.success && r.start ? new Date(r.start) : null,
          end: r.success && r.end ? new Date(r.end) : null,
          isAllDay: false,
          label: r.label || r.original,
          original: r.original,
          success: r.success,
          error: r.error,
          availability: r.availability ?? null,
          availablePeriods: r.availablePeriods ?? [],
        }))
        if (this.extractedDates.length === 0) {
          this.extractionError = "日時を解析できませんでした"
        }
      } catch (e: any) {
        this.extractionError = e.message || "日時の抽出に失敗しました"
        this.extractedDates = []
      } finally {
        this.isExtracting = false
      }
    },
    openCalendar(id: string, type: 'google' | 'outlook' | 'apple') {
      const date = this.extractedDates.find((d) => d.id === id)
      if (!date) return
      this.openCalendarByTime(date.start!, date.end!, type)
    },
    openCalendarByTime(start: Date, end: Date, type: 'google' | 'outlook' | 'apple') {
      const d = { start, end } as ExtractedDate
      if (type === 'google') {
        const url = toGoogleCalendarUrl(d)
        if (url) window.open(url, '_blank')
      } else if (type === 'outlook') {
        const url = toOutlookCalendarUrl(d)
        if (url) window.open(url, '_blank')
      } else if (type === 'apple') {
        const blob = toIcsBlob(d)
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'event.ics'
        a.click()
        URL.revokeObjectURL(url)
      }
    },
    async fetchEventsForAllCandidates() {
      const validDates = this.extractedDates.filter((d) => d.success && d.start && d.end)
      if (validDates.length === 0) return

      const starts = validDates.map((d) => d.start!)
      const ends = validDates.map((d) => d.end!)
      const rangeStart = startOfDay(min(starts))
      const rangeEnd = endOfDay(max(ends))

      this.isLoadingEvents = true
      try {
        this.cachedEvents = await fetchUserEvents(rangeStart, rangeEnd)
      } catch {
        this.cachedEvents = []
      } finally {
        this.isLoadingEvents = false
      }
    },
    async refreshAvailability() {
      if (!this.inputText.trim() || this.extractedDates.length === 0) return

      this.isLoadingEvents = true
      try {
        const results = await parse(this.inputText, browserTimeZone())
        for (const r of results) {
          const existing = this.extractedDates.find((d) => d.original === r.original)
          if (existing) {
            existing.availability = r.availability ?? null
            existing.availablePeriods = r.availablePeriods ?? []
          }
        }
        const validDates = this.extractedDates.filter((d) => d.success && d.start && d.end)
        if (validDates.length > 0) {
          const starts = validDates.map((d) => d.start!)
          const ends = validDates.map((d) => d.end!)
          this.cachedEvents = await fetchUserEvents(startOfDay(min(starts)), endOfDay(max(ends)))
        }
      } catch {
        // ignore
      } finally {
        this.isLoadingEvents = false
      }
    },
    reset() {
      this.inputText = ""
      this.extractedDates = []
      this.extractionError = null
      this.cachedEvents = []
      this.isLoadingEvents = false
    },
  },
  getters: {
    hasExtractedDates(): boolean {
      return this.extractedDates.length > 0
    },
    allAsPeriods(): PeriodWithId[] {
      return this.extractedDates
        .filter((d) => d.success && d.start && d.end)
        .map((d) => new PeriodWithId(d.start!, d.end!, d.id))
    },
    availabilityById(): (id: string) => 'free' | 'partial' | 'busy' | null {
      return (id: string) => {
        const d = this.extractedDates.find((d) => d.id === id)
        return d?.availability ?? null
      }
    },
  },
})
